import React from 'react';
import { AlertCircle, ExternalLink, Loader2 } from 'lucide-react';
import { OAuthConnection } from '../../services/RepositoryService';
import { getProviderColor, getProviderIcon } from './helpers';
import { ProviderTab } from './types';

export interface ConnectPromptProps {
  activeTab: ProviderTab;
  activeConnection: OAuthConnection | undefined;
  connectingProvider: ProviderTab | null;
  error: string | null;
  onConnect: (provider: ProviderTab) => void;
}

const titleCase = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

const errorTitle = (error: string): string => {
  if (error.includes('verify your email') || error.includes('Email verification')) {
    return 'Email Verification Required';
  }
  if (error.includes('reverification') || error.includes('additional verification')) {
    return 'Additional Verification Required';
  }
  return 'Connection Error';
};

const errorBody = (error: string): string => {
  if (error.includes('verify your email') || error.includes('Email verification')) {
    return 'Please verify your email address before connecting external accounts. Check your inbox for a verification link.';
  }
  return error;
};

export const ConnectPrompt: React.FC<ConnectPromptProps> = ({
  activeTab,
  activeConnection,
  connectingProvider,
  error,
  onConnect,
}) => {
  const isEmailVerificationError = Boolean(
    error && (error.includes('verify your email') || error.includes('Email verification')),
  );

  return (
    <div className="text-center py-16 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex justify-center mb-4">
        <div
          className={`p-4 rounded-full bg-gray-100 dark:bg-gray-700 ${getProviderColor(activeTab)}`}
        >
          {getProviderIcon(activeTab)}
        </div>
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        {activeConnection
          ? `Grant repository access to ${titleCase(activeTab)}`
          : `Connect your ${titleCase(activeTab)} account`}
      </h3>
      <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
        {activeConnection
          ? 'We need permission to read your repositories. Click below to grant the required access.'
          : `Connect your ${titleCase(activeTab)} account to access and analyze your repositories with Autar.`}
      </p>

      {error && (
        <div className="mb-6 mx-auto max-w-md p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div className="text-left">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200 mb-1">
                {errorTitle(error)}
              </p>
              <p className="text-sm text-amber-800 dark:text-amber-300 mb-2">{errorBody(error)}</p>
            </div>
          </div>
        </div>
      )}

      <button
        onClick={() => onConnect(activeTab)}
        disabled={connectingProvider === activeTab || isEmailVerificationError}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg transition-colors font-medium"
      >
        {connectingProvider === activeTab ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <ExternalLink className="w-5 h-5" />
        )}
        {connectingProvider === activeTab
          ? 'Connecting...'
          : activeConnection
            ? 'Grant Access'
            : `Connect ${titleCase(activeTab)}`}
      </button>
    </div>
  );
};
